import {
    GetClientErrorDetails,
    LogClientDiagnostic,
} from "source/app/services/ClientDiagnostics";
import { IsProduction } from "source/app/services/External/Environment/Environment";
import { SendClientErrorReportAsync } from "./SendClientErrorReport";
import { IsAuthorizationError } from "./ErrorBoundaryHelpers";

export type AutoErrorReportStatus = "idle" | "sending" | "sent" | "failed";

const autoReportStorageKey = "motw.clientAutoErrorReports";
const autoReportWindowMs = 10 * 60 * 1000;
const claimedSignatures = new Map<string, number>();

export function StartAutomaticClientErrorReport(
    error: any,
    errorInfo: any,
    onStatusChanged: (status: AutoErrorReportStatus, incidentId?: string) => void
) {
    if (!IsProduction() || !ClaimAutoReport(error, errorInfo)) {
        return;
    }

    LogClientDiagnostic(
        "client-error",
        "auto-report-request",
        {
            status: error?.response?.status,
            path: GetRequestPath(error?.config?.url),
        },
        "error"
    );
    onStatusChanged("sending");

    void SendClientErrorReportAsync(error, errorInfo, "automatic")
        .then((incidentId) => {
            LogClientDiagnostic("client-error", "auto-report-sent", {
                incidentId,
            });
            onStatusChanged("sent", incidentId);
        })
        .catch((reportError: unknown) => {
            LogClientDiagnostic(
                "client-error",
                "auto-report-error",
                { error: GetClientErrorDetails(reportError) },
                "error"
            );
            onStatusChanged("failed");
        });
}

function ClaimAutoReport(error: any, errorInfo: any) {
    const signature = GetErrorSignature(error, errorInfo);
    const now = Date.now();
    const claimedAt = claimedSignatures.get(signature);

    if (claimedAt !== undefined && now - claimedAt < autoReportWindowMs) {
        return false;
    }

    claimedSignatures.set(signature, now);

    try {
        const claims = GetStoredClaims().filter(
            (claim) => now - claim.claimedAt < autoReportWindowMs
        );

        if (claims.some((claim) => claim.signature === signature)) {
            return false;
        }

        claims.push({ signature, claimedAt: now });
        localStorage.setItem(autoReportStorageKey, JSON.stringify(claims));
    } catch (_error) {
        // The in-memory claim still prevents duplicate reports in this page.
    }

    return true;
}

function GetStoredClaims(): Array<{ signature: string; claimedAt: number }> {
    try {
        const value = JSON.parse(localStorage.getItem(autoReportStorageKey) || "[]");
        return Array.isArray(value)
            ? value.filter(
                  (claim) =>
                      typeof claim?.signature === "string" &&
                      typeof claim?.claimedAt === "number"
              )
            : [];
    } catch (_error) {
        return [];
    }
}

function GetErrorSignature(error: any, errorInfo: any) {
    const responseData = error?.response?.data;
    let responseText = "";

    try {
        responseText =
            typeof responseData === "string"
                ? responseData
                : JSON.stringify(responseData) || String(responseData);
    } catch (_error) {
        responseText = String(responseData);
    }

    // Concurrent protected requests share one authentication failure.
    if (IsAuthorizationError(error)) {
        return HashText(["authorization", error.response.status, responseText].join("\n"));
    }

    return HashText(
        [
            location.pathname,
            error?.name,
            error?.message,
            error?.stack,
            error?.response?.status,
            responseText,
            GetRequestPath(error?.config?.url),
            errorInfo?.componentStack,
        ].join("\n")
    );
}

function GetRequestPath(url: unknown) {
    if (typeof url !== "string") {
        return "";
    }

    try {
        return new URL(url, location.origin).pathname;
    } catch (_error) {
        return "";
    }
}

function HashText(value: string) {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(16);
}
